import React, { useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import { RatingsAndReviewMetric } from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewMetricTypes';
import { CustomXAxisLabel } from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewRatings/ProductCatalogCountRatingVolumeChart';
import {
  parseCumulativeProductCatalogCountRatingVolumeResponse,
  parseWeeklyProductCatalogCountData,
  useProductCatalogCountRatingData,
  useWeeklyProductCatalogCountData
} from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewsHooks';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { GenericXAxisLabel } from 'src/components/BeaconV2Components/TopEntityChartV2/GenericXAxisLabel';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import TopEntityChartV2Loading from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2Loading';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';
import {
  useAppSelector,
  useComparisonTimePeriodShortDisplayName,
  useMainTimePeriodShortDisplayName
} from 'src/utils/Hooks';
import { saveBarChartAsDelimitedData } from 'src/utils/chartUtils';
import { METRICTYPE } from 'src/utils/entityDefinitions';

const productCatalogCountOptions = [
  { label: 'Product Catalog Count by Cumulative', id: 'cumulativeProductCatalogCount' },
  { label: 'Product Catalog Count by Weekly', id: 'weeklyProductCatalogCount' }
];

/**
 * Product Catalog Count (by cumulative/ weekly) Review Volume Chart. This chart can be found on Review tab in Review
 *
 */
const ReviewProductCatalogCountByCumulativeVolumeChart = () => {
  const comparisonTimePeriodShortDisplayName = useComparisonTimePeriodShortDisplayName();
  const mainTimePeriodShortDisplayName = useMainTimePeriodShortDisplayName();
  const entityPhrase = useEntityPhrasing();
  const taxonomyText = useTaxonomySubtitleText();
  const [selectedMetric, setSelectedMetric] = useState<'cumulativeProductCatalogCount' | 'weeklyProductCatalogCount'>(
    'cumulativeProductCatalogCount'
  );
  const legendLabel = useAppSelector((state) => state.mainTimePeriod.displayName);
  const legendComparisonLabel = useAppSelector((state) => state.comparisonTimePeriod.displayName);
  const { startWeek: comparisonStartWeek, endWeek: comparisonEndWeek } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );

  const { data, isLoading: cumulativeLoading } = useProductCatalogCountRatingData(
    RatingsAndReviewMetric.CUMULATIVE_REVIEW_VOLUME
  );
  const { data: weeklyResponse, isLoading: weeklyLoading } = useWeeklyProductCatalogCountData({
    shouldStartFetch: selectedMetric === 'weeklyProductCatalogCount',
    fieldName: 'weeklyReviewVolume'
  });

  const { compareData, currentData } = data
    ? parseCumulativeProductCatalogCountRatingVolumeResponse({
        data,
        comparisonStartWeekToUse: comparisonStartWeek,
        comparisonEndWeek,
        selectedMetric,
        metricName: 'Reviews'
      })
    : { compareData: [], currentData: [] };

  const { compareData: weeklyCompareData, currentData: weeklyCurrentData } = weeklyResponse
    ? parseWeeklyProductCatalogCountData({ data: weeklyResponse?.data, metricName: 'Reviews' })
    : { compareData: [], currentData: [] };

  return (
    <BeaconChartWithLegend
      includeSummaryInfo={false}
      convertSeriesToDelimitedData={async () => {
        return saveBarChartAsDelimitedData(
          selectedMetric === 'cumulativeProductCatalogCount' ? currentData : weeklyCurrentData,
          selectedMetric === 'cumulativeProductCatalogCount' ? compareData : weeklyCompareData,
          { metricLabel: 'Product Catalog Count' }
        );
      }}
      subtitle={
        <BeaconSubtitleContainer>
          <Text variant="body1">
            {`The chart displays the number of products for ${entityPhrase} `}
            {taxonomyText} in each review volume tier
            {selectedMetric === 'cumulativeProductCatalogCount'
              ? '.'
              : ` during the
            ${mainTimePeriodShortDisplayName} period compared to the ${comparisonTimePeriodShortDisplayName}.`}
            <SubtitleFilters />
          </Text>
        </BeaconSubtitleContainer>
      }
      singleDropdownProps={{
        defaultLabel: '',
        options: productCatalogCountOptions,
        selectedId: selectedMetric,
        onChange: (option) => setSelectedMetric(option.id),
        suffix: 'Review Volume Tier'
      }}
      primaryLegendProps={{
        displayName: legendLabel
      }}
      comparisonLegendProps={{
        displayName: legendComparisonLabel
      }}
      loading={cumulativeLoading || weeklyLoading}
      loadingComponent={<TopEntityChartV2Loading />}
    >
      <TopEntityChartV2
        xAxisLabelTextStyles={{ lineHeight: 1.6 }}
        xAxisFormatter={(xAxisCategory) => {
          if (xAxisCategory.name === '400 and Above') {
            return ReactDOMServer.renderToStaticMarkup(<CustomXAxisLabel xAxisCategory={xAxisCategory} />);
          }
          return ReactDOMServer.renderToStaticMarkup(
            <GenericXAxisLabel xAxisCategory={xAxisCategory} wrapperStyle={{ width: '50px' }} />
          );
        }}
        chartSeries={[
          {
            type: 'column',
            data: selectedMetric === 'cumulativeProductCatalogCount' ? compareData : weeklyCompareData,
            metricType: METRICTYPE.VOLUME
          },
          {
            type: 'column',
            data: selectedMetric === 'cumulativeProductCatalogCount' ? currentData : weeklyCurrentData,
            metricType: METRICTYPE.VOLUME
          }
        ]}
      />
    </BeaconChartWithLegend>
  );
};

export default ReviewProductCatalogCountByCumulativeVolumeChart;
